<template>
  <div style="position: relative; display: inline-block;">
    <div class="form-control" @click="togglePicker" style="height: initial;text-align:center">
      <slot name="input" :startDate="start" :endDate="end" :ranges="ranges">
        <i class="glyphicon glyphicon-calendar fa fa-calendar" :class="{'text-muted': !showChange}"></i>&nbsp;
        <span v-if="showChange">{{rangeText}}</span>
        <span v-else class="text-muted">
          <span v-if="!singleDatePicker">00-00-00, 00:00 / 00-00-00, 00:00</span>
          <span v-else>00-00-00, 00:00</span>
        </span>
        <b class="caret"></b>
      </slot>
    </div>
    <label v-if="label" :class="{stay: true}" style="white-space: nowrap">{{label}}</label>
    <transition name="slide-fade" mode="out-in">
      <div
        class="daterangepicker dropdown-menu ltr"
        :class="pickerStyles"
        v-if="open"
        v-on-clickaway="clickAway"
      >
        <div class="calendars row no-gutters">
          <slot name="ranges" v-if="ranges !== false">
            <calendar-ranges class="col-12 col-md-auto" @clickRange="clickRange" :ranges="ranges"></calendar-ranges>
          </slot>

          <div class="drp-calendar col left" :class="{single: singleDatePicker}">
            <div class="daterangepicker_input d-none d-sm-block" v-if="false">
              <input
                class="input-mini form-control"
                type="text"
                name="daterangepicker_start"
                :value="startText"
              />
              <i class="fa fa-calendar glyphicon glyphicon-calendar"></i>
            </div>
            <div class="calendar-table">
              <calendar
                :monthDate="monthDate"
                :locale="locale"
                :start="start"
                :end="end"
                :minDate="min"
                :maxDate="max"
                :show-dropdowns="showDropdowns"
                @nextMonth="nextMonth"
                @prevMonth="prevMonth"
                @change-month="changeLeftMonth"
                @dateClick="dateClick"
                @hoverDate="hoverDate"
                :showWeekNumbers="showWeekNumbers"
              ></calendar>
            </div>
            <calendar-time
              v-if="timePicker"
              @update="onUpdateStartTime"
              :miniute-increment="timePickerIncrement"
              :hour24="timePicker24Hour"
              :second-picker="timePickerSeconds"
              :current-time="start"
            />
          </div>

          <div class="drp-calendar col right" v-if="!singleDatePicker">
            <div class="daterangepicker_input" v-if="false">
              <input
                class="input-mini form-control"
                type="text"
                name="daterangepicker_end"
                :value="endText"
              />
              <i class="fa fa-calendar glyphicon glyphicon-calendar"></i>
            </div>
            <div class="calendar-table">
              <calendar
                :monthDate="nextMonthDate"
                :locale="locale"
                :start="start"
                :end="end"
                :minDate="min"
                :maxDate="max"
                :show-dropdowns="showDropdowns"
                @nextMonth="nextMonth"
                @prevMonth="prevMonth"
                @change-month="changeRightMonth"
                @dateClick="dateClick"
                @hoverDate="hoverDate"
                :showWeekNumbers="showWeekNumbers"
              ></calendar>
            </div>
            <calendar-time
              v-if="timePicker"
              @update="onUpdateEndTime"
              :miniute-increment="timePickerIncrement"
              :hour24="timePicker24Hour"
              :second-picker="timePickerSeconds"
              :current-time="end"
            />
          </div>
        </div>

        <div class="drp-buttons" v-if="!autoApply">
          <span class="drp-selected">{{rangeText}}</span>
          <button
            v-if="!reset"
            class="cancelBtn btn btn-sm btn-default"
            type="button"
            @click="clickAway"
          >{{$t(locale.cancelLabel)}}</button>
          <button
            v-if="reset"
            class="cancelBtn btn btn-sm btn-default"
            type="button"
            @click="clickReset"
          >{{$t(locale.resetLabel)}}</button>
          <button
            class="applyBtn btn btn-sm btn-success"
            :disabled="in_selection"
            type="button"
            @click="clickedApply"
          >{{$t(locale.applyLabel)}}</button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import moment from "moment";
import Calendar from "./Calendar.vue";
import CalendarTime from "./CalendarTime";
import CalendarRanges from "./CalendarRanges";
import { nextMonth, prevMonth } from "./util";
import { mixin as clickaway } from "vue-clickaway";

export default {
  name: "dateRangePicker",
  components: { Calendar, CalendarTime, CalendarRanges },
  mixins: [clickaway],
  model: {
    prop: "dateRange",
    event: "update"
  },
  props: {
    minDate: {
      type: [String, Date],
      default() {
        return new Date(0);
      }
    },
    maxDate: {
      type: [String, Date],
      default() {
        return new Date(8640000000000000);
      }
    },
    showWeekNumbers: {
      type: Boolean,
      default: false
    },
    singleDatePicker: {
      type: Boolean,
      default: false
    },
    showDropdowns: {
      type: Boolean,
      default: false
    },
    timePicker: {
      type: Boolean,
      default: false
    },
    timePickerIncrement: {
      type: Number,
      default: 5
    },
    timePicker24Hour: {
      type: Boolean,
      default: true
    },
    timePickerSeconds: {
      type: Boolean,
      default: false
    },
    autoApply: {
      type: Boolean,
      default: false
    },
    localeData: {
      type: Object,
      default() {
        return {};
      }
    },
    dateRange: {
      // for v-model
      default: null
    },
    startDate: {
      default() {
        return new Date();
      }
    },
    endDate: {
      default() {
        return new Date();
      }
    },
    ranges: {
      type: [Object, Boolean],
      default() {
        return {
          "Last minute": [moment().subtract(1, "minute"), moment()],
          "Last 5 minutes": [moment().subtract(5, "minutes"), moment()],
          "Last 10 minutes": [moment().subtract(10, "minutes"), moment()],
          "Last 30 minutes": [moment().subtract(30, "minutes"), moment()],
          "Last hour": [moment().subtract(1, "hour"), moment()],
          "Last 6 hours": [moment().subtract(6, "hours"), moment()],
          "Last 24 hours": [moment().subtract(24, "hours"), moment()],
          "Last week": [
            moment()
              .subtract(1, "week")
              .startOf("week"),
            moment()
              .subtract(1, "week")
              .endOf("week")
          ],
          "Last month": [
            moment()
              .subtract(1, "month")
              .startOf("month"),
            moment()
              .subtract(1, "month")
              .endOf("month")
          ],
          "Last year": [moment().subtract(1, "year"), moment()],
          Today: [moment().startOf("day"), moment().endOf("day")],
          Yesterday: [
            moment()
              .subtract(1, "days")
              .startOf("day"),
            moment()
              .subtract(1, "days")
              .endOf("day")
          ],
          "This week": [moment().startOf("week"), moment().endOf("week")],
          "This month": [moment().startOf("month"), moment().endOf("month")],
          "This year": [moment().startOf("year"), moment().endOf("year")]
        };
      }
    },
    opens: {
      type: String,
      default: "center"
    },
    label: {
      type: String,
      default: ""
    },
    reset: {
      type: Boolean,
      default: false
    },
    id: {}
  },
  data() {
    let default_locale = {
      direction: "ltr",
      format: moment.localeData().longDateFormat("L"),
      separator: " / ",
      applyLabel: "Apply",
      cancelLabel: "Cancel",
      resetLabel: "Reset",
      weekLabel: "W",
      customRangeLabel: "Custom Range",
      daysOfWeek: moment.weekdaysMin(),
      monthNames: moment.monthsShort(),
      firstDay: moment.localeData().firstDayOfWeek()
    };

    // let data = { locale: _locale }
    let data = {
      locale: { ...default_locale, ...this.localeData },
      showChange: false
    };

    let startDate = this.startDate;
    let endDate = this.endDate;
    data.showChange = true;
    if (this.dateRange !== null) {
      if (this.dateRange.startDate && this.dateRange.endDate) {
        startDate = this.dateRange.startDate;
        endDate = this.dateRange.endDate;
        data.showChange = true;
      } else {
        startDate = new Date();
        endDate = new Date();
        data.showChange = false;
      }
    }

    data.monthDate = new Date(startDate);
    data.start = new Date(startDate);
    if (this.singleDatePicker) {
      // ignore endDate for singleDatePicker
      data.end = new Date(startDate);
    } else {
      data.end = new Date(endDate);
    }
    data.in_selection = false;
    data.open = false;

    // update day names order to firstDay
    if (data.locale.firstDay !== 0) {
      let iterator = data.locale.firstDay;
      while (iterator > 0) {
        data.locale.daysOfWeek.push(data.locale.daysOfWeek.shift());
        iterator--;
      }
    }
    return data;
  },
  methods: {
    changeMonth(newDate) {
      let max = new Date(this.max);
      let min = new Date(this.min);
      // check min
      if (moment(newDate).isBetween(min, max)) {
        this.monthDate = newDate;
        if (this.singleDatePicker && this.showDropdowns) {
          this.start = newDate;
          this.end = newDate;
        }
      } else if (moment(newDate).isAfter(max)) {
        this.monthDate = new Date(max);
        if (this.singleDatePicker && this.showDropdowns) {
          this.start = new Date(max);
          this.end = new Date(max);
        }
      } else {
        this.monthDate = new Date(min);
        if (this.singleDatePicker && this.showDropdowns) {
          this.start = new Date(min);
          this.end = new Date(min);
        }
      }
    },
    nextMonth() {
      this.changeMonth(
        nextMonth(
          new Date(this.monthDate.getFullYear(), this.monthDate.getMonth(), 1)
        )
      );
    },
    prevMonth() {
      this.changeMonth(
        prevMonth(
          new Date(this.monthDate.getFullYear(), this.monthDate.getMonth(), 1)
        )
      );
    },
    changeLeftMonth(value) {
      let newDate = new Date(value.year, value.month, 1);
      this.changeMonth(newDate);
    },
    changeRightMonth(value) {
      if (value.month <= 0) {
        value.month = 11;
        value.year -= 1;
      } else {
        value.month -= 1;
      }
      this.changeMonth(value);
    },
    normalizeDatetime(value, oldValue) {
      let newDate = new Date(value);
      if (this.timePicker) {
        newDate.setHours(oldValue.getHours());
        newDate.setMinutes(oldValue.getMinutes());
        newDate.setSeconds(oldValue.getSeconds());
        newDate.setMilliseconds(oldValue.getMilliseconds());
      }

      return newDate;
    },
    dateClick(value) {
      if (this.in_selection) {
        this.in_selection = false;
        this.end = this.normalizeDatetime(value, this.end);

        if (this.end < this.start) {
          this.in_selection = true;
          this.start = this.normalizeDatetime(value, this.start);
        }
        if (!this.in_selection && this.autoApply) {
          this.clickedApply();
        }
      } else {
        this.start = this.normalizeDatetime(value, this.start);
        this.end = this.normalizeDatetime(value, this.end);
        if (!this.singleDatePicker) {
          this.in_selection = true;
        } else if (this.autoApply) {
          this.clickedApply();
        }
      }
    },
    hoverDate(value) {
      let dt = this.normalizeDatetime(value, this.end);
      if (this.in_selection && dt > this.start) this.end = dt;
    },
    togglePicker() {
      this.open = !this.open;
    },
    applyValues(start, end) {
      this.start = start;
      this.end = end;
      this.open = false;
      this.showChange = true;
      const update = { startDate: null, endDate: null };
      if (typeof this.id != "undefined") {
        update.id = this.id;
      }
      this.$emit("update", update);
    },
    clickReset() {
      this.start = new Date();
      this.end = new Date();
      this.open = false;
      this.showChange = false;
      const update = { startDate: null, endDate: null };
      if (typeof this.id != "undefined") {
        update.id = this.id;
      }
      this.$emit("update", update);
    },
    clickedApply() {
      if (this.reset) {
        this.showChange = true;
      }
      this.open = false;
      const update = { startDate: this.start, endDate: this.end };
      if (typeof this.id != "undefined") {
        update.id = this.id;
      }
      this.$emit("update", update);
    },
    clickAway() {
      if (this.open) {
        // reset start and end
        let startDate = this.startDate;
        let endDate = this.endDate;
        if (this.dateRange !== null) {
          startDate = this.dateRange.startDate;
          endDate = this.dateRange.endDate;
        }
        this.start = new Date(startDate);
        this.end = new Date(endDate);
        this.open = false;
      }
    },
    clickRange(value) {
      this.start = new Date(value[0]);
      this.end = new Date(value[1]);
      this.monthDate = new Date(value[0]);
      //if (this.autoApply) this.clickedApply();
      this.clickedApply();
    },
    onUpdateStartTime(value) {
      let start = new Date(this.start);
      start.setHours(value.hours);
      start.setMinutes(value.minutes);
      start.setSeconds(value.seconds);

      this.start = start;
    },
    onUpdateEndTime(value) {
      let end = new Date(this.end);
      end.setHours(value.hours);
      end.setMinutes(value.minutes);
      end.setSeconds(value.seconds);

      this.end = end;
    }
  },
  computed: {
    nextMonthDate() {
      return nextMonth(this.monthDate);
    },
    startText() {
      return moment(this.start).format(this.locale.format);
    },
    endText() {
      return moment(new Date(this.end)).format(this.locale.format);
    },
    startTextNano() {
      return moment(this.start).format("x000000");
    },
    endTextNano() {
      return moment(new Date(this.end)).format("x000000");
    },
    rangeText() {
      let range = this.startText;
      if (!this.singleDatePicker) {
        range += " " + this.locale.separator + " " + this.endText;
      }
      return range;
    },
    nanoText() {
      let range = this.startTextNano;
      if (!this.singleDatePicker) {
        range += " " + this.locale.separator + " " + this.endTextNano;
      }
      return range;
    },
    min() {
      return this.minDate ? new Date(this.minDate) : null;
    },
    max() {
      return this.maxDate ? new Date(this.maxDate) : null;
    },
    pickerStyles() {
      return {
        "show-calendar": this.open,
        "show-ranges": !!this.ranges,
        "show-weeknumbers": this.showWeekNumbers,
        single: this.singleDatePicker,
        opensright: this.opens === "right",
        opensleft: this.opens === "left",
        openscenter: this.opens === "center"
      };
    }
  },
  watch: {
    startDate(value) {
      this.start = new Date(value);
      this.showChange = true;
    },
    endDate(value) {
      this.end = new Date(value);
      this.showChange = true;
    },
    minDate(value) {
      this.changeMonth(this.monthDate);
    },
    maxDate(value) {
      this.changeMonth(this.monthDate);
    },
    dateRange(value) {
      if (value) {
        if (value.startDate) {
          this.start = new Date(value.startDate);
        }
        if (value.endDate) {
          this.end = new Date(value.endDate);
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
$week-width: 682px - 628px;

.reportrange-text {
  background: #fff;
  cursor: pointer;
  padding: 5px 10px;
  border: 1px solid #ccc;
  width: 100%;
}

.daterangepicker {
  flex-direction: column;
  display: flex;
  width: auto;

  @media screen and (max-width: 768px) {
    &.show-ranges {
      .drp-calendar.left {
        border-left: 0px;
      }

      .ranges {
        border-bottom: 1px solid #ddd;

        ::v-deep ul {
          display: flex;
          flex-wrap: wrap;
          width: auto;
        }
      }
    }
  }

  @media screen and (min-width: 540px) {
    min-width: 486px;
    &.show-weeknumbers {
      min-width: 490px + $week-width;
    }
  }

  @media screen and (min-width: 768px) {
    &.show-ranges {
      min-width: 628px;

      &.show-weeknumbers {
        min-width: 628px + $week-width;
      }
    }
  }

  &.single {
    @media screen and (max-width: 340px) {
      min-width: 250px;

      &.show-weeknumbers {
        min-width: 250px + $week-width;
      }
    }

    @media screen and (min-width: 339px) {
      min-width: auto;
      &.show-ranges {
        min-width: 328px;

        &.show-weeknumbers {
          min-width: 328px + $week-width;
        }

        .drp-calendar.left {
          border-left: 1px solid #ddd;
        }

        .ranges {
          width: auto;
          max-width: none;
          flex-basis: auto;
          border-bottom: 0;

          ::v-deep ul {
            display: block;
            width: 100%;
          }
        }
      }
    }
  }

  &.show-calendar {
    display: block;
  }
}

div.daterangepicker.opensleft {
  top: 35px;
  right: 10px;
  left: auto;
}

div.daterangepicker.openscenter {
  top: 35px;
  right: auto;
  left: 50%;
  transform: translate(-50%, 0);
}

div.daterangepicker.opensright {
  top: 35px;
  left: 10px;
  right: auto;
}

/* Enter and leave animations can use different */
/* durations and timing functions.              */
.slide-fade-enter-active {
  transition: all 0.2s ease;
}

.slide-fade-leave-active {
  transition: all 0.1s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter, .slide-fade-leave-to
        /* .slide-fade-leave-active for <2.1.8 */
 {
  transform: translateX(10px);
  opacity: 0;
}

.green-border {
  border-color: #c0f0eb;
}

label.animate {
  transition: 0.15s ease-out all;
}

.form-control + label {
  display: block;
  cursor: text;
  color: #aaa;
  position: absolute;
  top: 0.65rem;
  left: 1rem;
  pointer-events: none;
}
.form-control.form-control-sm + label {
  top: 0.5rem;
}
.form-control:focus + label,
label.stay {
  top: -0.75em;
  left: 1em;
  font-size: 0.8em;
  color: #5fccff;
  background-color: #fff;
  padding: 0 0.3rem;
}
label.stay {
  color: #afb4d2;
}
.form-control.form-control-sm + label.stay {
  top: -0.75em;
}
</style>
